import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import type {
  PCloseStepPhase,
  PCloseStepStatus,
  PeriodCloseChecklistResponse,
  PeriodCloseStep,
  PeriodMaster,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useClosePeriod,
  usePeriodCloseChecklist,
  useUpdatePeriodCloseStep,
} from '../lib/queries/period-close';
import { usePeriods } from '../lib/queries/periods';

/* ===== Display types =====
 * The backend speaks UPPERCASE enums (PRE / DONE). The prototype styling and
 * Thai labels live entirely on the lowercase display side ('pre' / 'done').
 * The two `toUi*` helpers below are the only translation boundary.
 */
type UIStatus = 'done' | 'in_progress' | 'pending' | 'blocked' | 'na';
type UIPhase = 'pre' | 'recur' | 'recon' | 'report' | 'approval' | 'lock';
type TabKey = 'current' | 'history' | 'calendar' | 'settings';
type StatusBadge = 'positive' | 'warning' | 'neutral' | 'negative';
type EventType = 'start' | 'task' | 'review' | 'lock';

interface PhaseDef {
  code: UIPhase;
  label: string;
  icon: string;
  desc: string;
}

interface StatusInfo {
  label: string;
  c: StatusBadge;
  dot: string;
}

interface CalendarEvent {
  type: EventType;
  label: string;
}

const PHASE_TO_UI: Record<PCloseStepPhase, UIPhase> = {
  PRE: 'pre',
  RECUR: 'recur',
  RECON: 'recon',
  REPORT: 'report',
  APPROVAL: 'approval',
  LOCK: 'lock',
};

const STATUS_TO_UI: Record<PCloseStepStatus, UIStatus> = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  BLOCKED: 'blocked',
  NA: 'na',
};

const PHASES: readonly PhaseDef[] = [
  { code: 'pre', label: '1. Pre-close', icon: '📋', desc: 'ตรวจสอบความครบถ้วน' },
  { code: 'recur', label: '2. Recurring Entries', icon: '🔄', desc: 'รัน Adjusting อัตโนมัติ' },
  { code: 'recon', label: '3. Reconciliations', icon: '⚖️', desc: 'กระทบยอด' },
  { code: 'report', label: '4. Reports & Review', icon: '📊', desc: 'ออกรายงาน' },
  { code: 'approval', label: '5. Approval', icon: '✅', desc: 'อนุมัติงบ' },
  { code: 'lock', label: '6. Lock & Forward', icon: '🔒', desc: 'ปิด & Roll Forward' },
];

const STATUS_INFO: Readonly<Record<UIStatus, StatusInfo>> = {
  done: { label: 'เสร็จแล้ว', c: 'positive', dot: 'var(--positive)' },
  in_progress: { label: 'กำลังทำ', c: 'warning', dot: 'var(--warning)' },
  pending: { label: 'รอทำ', c: 'neutral', dot: '#e5e5e9' },
  blocked: { label: 'ติดขัด', c: 'negative', dot: 'var(--negative)' },
  na: { label: 'ไม่ต้องทำ', c: 'neutral', dot: '#e5e5e9' },
};

const THAI_MONTHS_SHORT: readonly string[] = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

const SCHEDULE_WEEKDAYS: readonly string[] = [
  'ศ',
  'ส',
  'อา',
  'จ',
  'อ',
  'พ',
  'พฤ',
  'ศ',
  'ส',
  'อา',
  'จ',
];

const SETTINGS_OWNERS: readonly string[] = ['สมหญิง ว.', 'สมชาย ก.', 'อรอุมา ส.', 'ปรีชา ก.'];

const CAL_EVENTS: Readonly<Record<number, CalendarEvent>> = {
  21: { type: 'start', label: 'เริ่ม Process ปิดงวด' },
  22: { type: 'task', label: 'Pre-close + Recurring' },
  23: { type: 'task', label: 'Recurring (ต่อ)' },
  24: { type: 'task', label: 'Recurring (จบ)' },
  25: { type: 'task', label: 'Reconciliation' },
  26: { type: 'task', label: 'Bank Recon' },
  27: { type: 'task', label: 'Tax Recon' },
  28: { type: 'task', label: 'Reports' },
  29: { type: 'review', label: 'ผจก. บัญชี ตรวจ' },
  30: { type: 'review', label: 'ผจก. การเงิน อนุมัติ' },
  31: { type: 'lock', label: 'CFO อนุมัติ + Lock' },
};

const EVENT_COLORS: Readonly<Record<EventType, string>> = {
  start: '#1a56a6',
  task: '#117a3d',
  review: '#b54708',
  lock: '#7030A0',
};

const ACTION_AFTER_LOCK: readonly string[] = [
  'Lock JV ในงวดนี้ · ไม่อนุญาตให้ Post / Edit / Delete',
  'Generate Carry Forward ยอดยกมาเดือนถัดไปทุก Balance Sheet account',
  'Email สรุปงบการเงินถึงผู้บริหาร 3 บริษัท',
  'แจ้งเตือนทีมว่างวดถัดไปเปิดใช้งานแล้ว',
  'บันทึก Audit Trail · ใครปิดงวด · วันเวลา · IP Address',
  'งวดถัดไปจะเริ่ม process ปิดอัตโนมัติในวันที่ 21 ของเดือน',
];

const HISTORY_ADJ_COUNTS: readonly number[] = [14, 13, 16, 15, 12, 17, 13, 14];

const STATUS_ACTIONS: readonly { value: PCloseStepStatus; label: string }[] = [
  { value: 'DONE', label: 'ทำเสร็จ' },
  { value: 'IN_PROGRESS', label: 'กำลังทำ' },
  { value: 'BLOCKED', label: 'ติดขัด' },
  { value: 'NA', label: 'ไม่ต้องทำ' },
  { value: 'PENDING', label: 'รีเซ็ต' },
];

function getStatusInfo(s: UIStatus): StatusInfo {
  return STATUS_INFO[s] ?? STATUS_INFO.pending;
}

function periodToDisplay(period: string): { mm: string; yyyy: string; thaiMonth: string } {
  // YYYYMM -> { mm: '05', yyyy: '2026', thaiMonth: 'พ.ค.' }
  const yyyy = period.slice(0, 4);
  const mm = period.slice(4, 6);
  const idx = Number.parseInt(mm, 10) - 1;
  return { mm, yyyy, thaiMonth: THAI_MONTHS_SHORT[idx] ?? '' };
}

// Pick the period whose checklist the page is showing. Default = the
// chronologically first OPEN period (i.e. the in-progress month). If none
// is OPEN yet, fall back to current month so the auto-init path still
// produces a checklist row to show.
function pickDefaultPeriod(periods: PeriodMaster[] | undefined): string | undefined {
  if (!periods || periods.length === 0) return undefined;
  const open = periods
    .filter((p) => p.status === 'OPEN')
    .sort((a, b) => a.period.localeCompare(b.period));
  if (open.length > 0) return open[0]!.period;
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/* ===== Route ===== */
export const Route = createFileRoute('/pclose')({
  component: PClosePage,
});

function PClosePage() {
  const companyId = useActiveCompanyId();
  const periodsQ = usePeriods(companyId);
  const periods = periodsQ.data;

  const [tab, setTab] = useState<TabKey>('current');
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(undefined);
  const [showRunRecur, setShowRunRecur] = useState<PeriodCloseStep | null>(null);
  const [showClose, setShowClose] = useState<boolean>(false);

  const effectivePeriod = selectedPeriod ?? pickDefaultPeriod(periods);

  const checklistQ = usePeriodCloseChecklist(companyId, effectivePeriod);
  const updateStep = useUpdatePeriodCloseStep(companyId, effectivePeriod);
  const closePeriod = useClosePeriod(companyId, effectivePeriod);

  const data = checklistQ.data;
  const summary = data?.summary;

  const closedPeriods = useMemo(
    () => (periods ?? []).filter((p) => p.status === 'CLOSED'),
    [periods],
  );

  const periodDisplay = effectivePeriod ? periodToDisplay(effectivePeriod) : null;
  const closeButtonLabel = periodDisplay
    ? `ปิดงวด ${periodDisplay.thaiMonth} ${periodDisplay.yyyy}`
    : 'ปิดงวด';

  const tabs: readonly (readonly [TabKey, string])[] = [
    [
      'current',
      periodDisplay
        ? `Checklist งวด ${periodDisplay.thaiMonth} ${periodDisplay.yyyy}`
        : 'Checklist งวดปัจจุบัน',
    ],
    ['history', 'ประวัติการปิดงวด'],
    ['calendar', 'Calendar & Schedule'],
    ['settings', 'ตั้งค่า Checklist'],
  ];

  const periodIsClosed = data?.periodStatus === 'CLOSED';
  const criticalOpen = summary?.criticalOpen ?? 0;
  const canClose = !periodIsClosed && criticalOpen === 0 && !closePeriod.isPending;

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'ปิดงวดบัญชี']}
        title="ปิดงวดบัญชี (Period Close Workflow)"
        actions={
          <>
            <button className="btn">
              <Icon name="download" size={14} /> Export Checklist
            </button>
            <button className="btn primary" onClick={() => setShowClose(true)} disabled={!canClose}>
              <Icon name="check" size={14} /> {closeButtonLabel}
            </button>
          </>
        }
      />

      {checklistQ.isError && (
        <div
          style={{
            margin: '0 0 var(--section-gap)',
            padding: '12px 16px',
            background: '#fce9e7',
            border: '1px solid var(--negative)',
            borderRadius: 8,
            color: 'var(--negative)',
          }}
        >
          ไม่สามารถโหลด Checklist ได้ — {checklistQ.error.message}
        </div>
      )}

      {/* Period selector left-rail: closed (read-only), in-progress (default), future (greyed). */}
      <PeriodRail
        periods={periods ?? []}
        selectedPeriod={effectivePeriod}
        onSelect={(p) => setSelectedPeriod(p)}
      />

      <PeriodKpiGrid
        summary={summary}
        periodDisplay={periodDisplay}
        isLoading={checklistQ.isLoading}
      />

      {/* Phase progress strip */}
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div style={{ padding: '16px 18px' }}>
          <div className="section-title" style={{ margin: '0 0 14px' }}>
            Workflow Progress · 6 Phases
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10 }}>
            {PHASES.map((ph) => {
              const phSteps = (data?.steps ?? []).filter((s) => PHASE_TO_UI[s.phase] === ph.code);
              const phDone = phSteps.filter((s) => s.status === 'DONE').length;
              const phTotal = phSteps.length;
              const phPct = phTotal > 0 ? Math.round((phDone / phTotal) * 100) : 0;
              const complete = phPct === 100 && phTotal > 0;
              const active = phPct > 0 && phPct < 100;
              return (
                <div
                  key={ph.code}
                  style={{
                    padding: '14px',
                    border: `1.5px solid ${complete ? 'var(--positive)' : active ? 'var(--brand)' : 'var(--border)'}`,
                    borderRadius: 8,
                    background: complete ? '#e7f4ec' : active ? 'var(--brand-tint)' : 'white',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{ph.icon}</span>
                    {complete && (
                      <span style={{ color: 'var(--positive)', fontSize: 18, fontWeight: 700 }}>
                        ✓
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{ph.label}</div>
                  <div className="meta" style={{ marginTop: 2 }}>
                    {ph.desc}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        background: '#f0f0f3',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${phPct}%`,
                          height: '100%',
                          background: complete
                            ? 'var(--positive)'
                            : active
                              ? 'var(--brand)'
                              : '#e5e5e9',
                        }}
                      />
                    </div>
                    <span
                      style={{ fontSize: 13, fontWeight: 600, minWidth: 48, textAlign: 'right' }}
                    >
                      {phDone}/{phTotal}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'current' && (
        <CurrentChecklist
          data={data}
          isLoading={checklistQ.isLoading}
          periodIsClosed={periodIsClosed}
          onRunRecur={setShowRunRecur}
          onUpdateStatus={(stepNo, status) => updateStep.mutate({ stepNo, status })}
          isUpdating={updateStep.isPending}
        />
      )}
      {tab === 'history' && <CloseHistory closedPeriods={closedPeriods} />}
      {tab === 'calendar' && <CloseCalendar />}
      {tab === 'settings' && <CloseSettings data={data} />}

      {showRunRecur && (
        <RunRecurringModal step={showRunRecur} onClose={() => setShowRunRecur(null)} />
      )}
      {showClose && (
        <ClosePeriodModal
          summary={summary}
          periodDisplay={periodDisplay}
          isClosing={closePeriod.isPending}
          closeError={closePeriod.error}
          onConfirm={() => {
            closePeriod.mutate(undefined, {
              onSuccess: () => setShowClose(false),
            });
          }}
          onClose={() => setShowClose(false)}
        />
      )}
    </div>
  );
}

/* ===== Period rail ===== */
interface PeriodRailProps {
  periods: readonly PeriodMaster[];
  selectedPeriod: string | undefined;
  onSelect: (period: string) => void;
}

function PeriodRail({ periods, selectedPeriod, onSelect }: PeriodRailProps) {
  if (periods.length === 0) return null;
  const sorted = [...periods].sort((a, b) => a.period.localeCompare(b.period));
  return (
    <div
      style={{
        marginBottom: 'var(--section-gap)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--text-faint)', marginRight: 6 }}>เลือกงวด:</span>
      {sorted.map((p) => {
        const isSelected = p.period === selectedPeriod;
        const isClosed = p.status === 'CLOSED';
        const isFuture = p.status === 'FUTURE';
        const disp = periodToDisplay(p.period);
        const bg = isSelected
          ? 'var(--brand)'
          : isClosed
            ? '#fafafa'
            : isFuture
              ? '#f5f5f7'
              : 'var(--brand-tint)';
        const color = isSelected ? 'white' : isFuture ? 'var(--text-faint)' : 'inherit';
        const border = isSelected ? 'var(--brand)' : isClosed ? 'var(--border)' : 'var(--brand)';
        return (
          <button
            key={p.period}
            type="button"
            onClick={() => onSelect(p.period)}
            disabled={isFuture}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${border}`,
              background: bg,
              color,
              fontSize: 13,
              fontWeight: isSelected ? 600 : 500,
              cursor: isFuture ? 'not-allowed' : 'pointer',
              opacity: isFuture ? 0.6 : 1,
            }}
          >
            {disp.mm}/{disp.yyyy}
            {isClosed && <span style={{ marginLeft: 6, fontSize: 11 }}>🔒</span>}
            {isFuture && <span style={{ marginLeft: 6, fontSize: 11 }}>ยังไม่เปิด</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ===== KPI grid ===== */
interface PeriodKpiGridProps {
  summary: PeriodCloseChecklistResponse['summary'] | undefined;
  periodDisplay: { mm: string; yyyy: string; thaiMonth: string } | null;
  isLoading: boolean;
}

function PeriodKpiGrid({ summary, periodDisplay, isLoading }: PeriodKpiGridProps) {
  const placeholder = isLoading || !summary;
  const pct = summary?.pct ?? 0;
  const done = summary?.done ?? 0;
  const total = summary?.total ?? 24;
  const inProgress = summary?.inProgress ?? 0;
  const criticalOpen = summary?.criticalOpen ?? 0;
  const headerValue = periodDisplay ? `${periodDisplay.mm}/${periodDisplay.yyyy}` : '—';
  return (
    <div className="kpi-grid">
      <div className="kpi">
        <div className="label">
          <span className="dot" />
          งวดที่กำลังปิด
        </div>
        <div className="value tnum" style={{ fontSize: 24 }}>
          {headerValue} <span className="unit">{periodDisplay?.thaiMonth ?? ''}</span>
        </div>
        <div className="trend faint">
          {placeholder ? 'กำลังโหลด…' : `เริ่ม Process เมื่อ 21 ของเดือน · ${total} ขั้นตอน`}
        </div>
      </div>
      <div className="kpi">
        <div className="label">
          <span className="dot" style={{ background: 'var(--brand)' }} />
          ความคืบหน้า
        </div>
        <div className="value tnum">
          {pct}
          <span className="unit">%</span>
        </div>
        <div className="trend faint">
          {done} / {total} ขั้นตอน
        </div>
      </div>
      <div className="kpi">
        <div className="label">
          <span className="dot" style={{ background: 'var(--positive)' }} />
          เสร็จแล้ว
        </div>
        <div className="value tnum" style={{ color: 'var(--positive)' }}>
          {done} <span className="unit">ขั้น</span>
        </div>
        <div className="trend pos">{inProgress} กำลังทำ</div>
      </div>
      <div className="kpi">
        <div className="label">
          <span
            className="dot"
            style={{ background: criticalOpen ? 'var(--negative)' : 'var(--positive)' }}
          />
          ติดขั้นวิกฤต
        </div>
        <div
          className="value tnum"
          style={{ color: criticalOpen ? 'var(--negative)' : 'var(--positive)' }}
        >
          {criticalOpen} <span className="unit">ขั้น</span>
        </div>
        <div className="trend down">
          {criticalOpen > 0 ? 'ปิดงวดไม่ได้จนกว่าจะเสร็จ' : 'พร้อมปิดงวด'}
        </div>
      </div>
    </div>
  );
}

/* ===== Current Checklist ===== */
interface CurrentChecklistProps {
  data: PeriodCloseChecklistResponse | undefined;
  isLoading: boolean;
  periodIsClosed: boolean;
  onRunRecur: (s: PeriodCloseStep) => void;
  onUpdateStatus: (stepNo: number, status: PCloseStepStatus) => void;
  isUpdating: boolean;
}

function CurrentChecklist({
  data,
  isLoading,
  periodIsClosed,
  onRunRecur,
  onUpdateStatus,
  isUpdating,
}: CurrentChecklistProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      {PHASES.map((ph) => {
        const phSteps = (data?.steps ?? []).filter((s) => PHASE_TO_UI[s.phase] === ph.code);
        const done = phSteps.filter((s) => s.status === 'DONE').length;
        const total = phSteps.length;
        return (
          <div key={ph.code} className="card">
            <div className="card-head">
              <div>
                <h3>
                  <span style={{ fontSize: 22, marginRight: 8 }}>{ph.icon}</span>
                  {ph.label}
                </h3>
                <div className="sub">
                  {ph.desc} · {done} / {total} ขั้นเสร็จแล้ว
                </div>
              </div>
              <span
                className={`badge ${done === total && total > 0 ? 'positive' : done > 0 ? 'warning' : 'neutral'}`}
              >
                {done === total && total > 0
                  ? '✓ เสร็จ'
                  : done > 0
                    ? `${done}/${total}`
                    : 'รอเริ่ม'}
              </span>
            </div>
            <div className="card-body flush">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>ลำดับ</th>
                    <th>ขั้นตอน</th>
                    <th>ความสำคัญ</th>
                    <th className="num">รายการ</th>
                    <th className="num">มูลค่า</th>
                    <th>เวลาประมาณ</th>
                    <th>สถานะ</th>
                    <th style={{ width: 200 }}>การกระทำ</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && phSteps.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="muted"
                        style={{ padding: 20, textAlign: 'center' }}
                      >
                        กำลังโหลด…
                      </td>
                    </tr>
                  )}
                  {phSteps.map((s) => {
                    const uiStatus = STATUS_TO_UI[s.status];
                    const info = getStatusInfo(uiStatus);
                    const rowBg =
                      uiStatus === 'in_progress'
                        ? '#fef3e8'
                        : uiStatus === 'done'
                          ? '#fafafa'
                          : undefined;
                    const circleColor =
                      uiStatus === 'done' || uiStatus === 'in_progress' || uiStatus === 'blocked'
                        ? 'white'
                        : '#52525b';
                    const circleLabel =
                      uiStatus === 'done'
                        ? '✓'
                        : uiStatus === 'in_progress'
                          ? '…'
                          : uiStatus === 'blocked'
                            ? '!'
                            : s.stepNo;
                    return (
                      <tr key={s.id} style={rowBg ? { background: rowBg } : undefined}>
                        <td className="num">
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              background: info.dot,
                              color: circleColor,
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 13,
                              fontWeight: 700,
                              fontFamily: 'var(--font-display)',
                            }}
                          >
                            {circleLabel}
                          </div>
                        </td>
                        <td>
                          <b>{s.name}</b>
                          {s.critical && (
                            <span
                              className="badge negative"
                              style={{ fontSize: 13, marginLeft: 8 }}
                            >
                              Critical
                            </span>
                          )}
                          {s.notes && (
                            <div className="meta" style={{ marginTop: 4, fontStyle: 'italic' }}>
                              💬 {s.notes}
                            </div>
                          )}
                          {s.completedAt && s.completedBy && (
                            <div className="meta" style={{ marginTop: 4 }}>
                              เสร็จเมื่อ {fmtDate(s.completedAt)} โดย {s.completedBy}
                            </div>
                          )}
                        </td>
                        <td className="muted">
                          {s.critical ? 'ต้องเสร็จก่อนปิดงวด' : 'แนะนำให้ทำ'}
                        </td>
                        <td className="num tnum">{s.refCount > 0 ? fmt(s.refCount) : '—'}</td>
                        <td className="num tnum">
                          {Number(s.amount) > 0 ? fmt(Number(s.amount)) : '—'}
                        </td>
                        <td className="muted">~{s.estMin} นาที</td>
                        <td>
                          <span className={`badge ${info.c}`}>
                            <span className="bdot" />
                            {info.label}
                          </span>
                        </td>
                        <td>
                          <StepActionMenu
                            step={s}
                            uiStatus={uiStatus}
                            disabled={periodIsClosed || isUpdating}
                            onRunRecur={onRunRecur}
                            onUpdateStatus={onUpdateStatus}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface StepActionMenuProps {
  step: PeriodCloseStep;
  uiStatus: UIStatus;
  disabled: boolean;
  onRunRecur: (s: PeriodCloseStep) => void;
  onUpdateStatus: (stepNo: number, status: PCloseStepStatus) => void;
}

function StepActionMenu({
  step,
  uiStatus,
  disabled,
  onRunRecur,
  onUpdateStatus,
}: StepActionMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
      {uiStatus === 'pending' && step.linkRoute && (
        <button
          className="btn sm primary"
          style={{ height: 28, fontSize: 14 }}
          disabled={disabled}
          onClick={() => onRunRecur(step)}
        >
          รัน →
        </button>
      )}
      <button
        type="button"
        className="btn sm ghost"
        style={{ height: 28, fontSize: 13 }}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        เปลี่ยนสถานะ ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 32,
            right: 0,
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50,
            minWidth: 130,
            padding: 4,
          }}
        >
          {STATUS_ACTIONS.map((a) => (
            <button
              key={a.value}
              type="button"
              onClick={() => {
                onUpdateStatus(step.stepNo, a.value);
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '6px 10px',
                fontSize: 13,
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--brand-tint)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===== Close History — closed periods from live API + filler rows ===== */
interface CloseHistoryProps {
  closedPeriods: readonly PeriodMaster[];
}

function CloseHistory({ closedPeriods }: CloseHistoryProps) {
  const sorted = [...closedPeriods].sort((a, b) => b.period.localeCompare(a.period));
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>ประวัติการปิดงวด — 12 เดือนล่าสุด</span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>งวด</th>
            <th>วันที่ปิด</th>
            <th className="num">วันที่ใช้</th>
            <th>ผู้ปิด</th>
            <th>ผู้อนุมัติ</th>
            <th className="num">Adjusting JV</th>
            <th className="num">Critical Issue</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, i) => {
            const disp = periodToDisplay(p.period);
            return (
              <tr key={p.period}>
                <td>
                  <b>
                    {disp.mm}/{disp.yyyy}
                  </b>
                  <div className="meta">
                    {disp.thaiMonth} {disp.yyyy}
                  </div>
                </td>
                <td>{p.closedDate ? fmtDate(p.closedDate) : '—'}</td>
                <td className="num">— วัน</td>
                <td className="muted">{p.closedBy ?? '—'}</td>
                <td className="muted">CFO</td>
                <td className="num">{HISTORY_ADJ_COUNTS[i] ?? 14}</td>
                <td className="num">
                  <span className="badge positive" style={{ fontSize: 13 }}>
                    0
                  </span>
                </td>
                <td>
                  <span className="badge positive">
                    <Icon name="check" size={10} /> Closed · Locked
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn sm ghost" style={{ height: 26, fontSize: 13 }}>
                      ดู Checklist
                    </button>
                    <button className="btn sm ghost" style={{ height: 26, fontSize: 13 }}>
                      Audit Log
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                ยังไม่มีงวดที่ปิด
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ===== Close Calendar — static layout retained (prototype-fidelity) ===== */
function CloseCalendar() {
  const monthDays: readonly string[] = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
  const startDay = 4;
  const days = 31;
  const today = 25;

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>Calendar — พฤษภาคม 2026</h3>
        </div>
        <div className="card-body">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {monthDays.map((d) => (
                  <th
                    key={d}
                    style={{
                      padding: 8,
                      fontSize: 14,
                      color: 'var(--text-muted)',
                      fontWeight: 500,
                    }}
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, w) => (
                <tr key={w}>
                  {Array.from({ length: 7 }, (_, d) => {
                    const dayNum = w * 7 + d - startDay + 1;
                    if (dayNum < 1 || dayNum > days) {
                      return <td key={d} style={{ padding: 4 }} />;
                    }
                    const ev = CAL_EVENTS[dayNum];
                    const isToday = dayNum === today;
                    return (
                      <td key={d} style={{ padding: 4, verticalAlign: 'top' }}>
                        <div
                          style={{
                            height: 64,
                            padding: 6,
                            border: isToday ? '2px solid var(--brand)' : '1px solid var(--border)',
                            borderRadius: 6,
                            background: ev ? `${EVENT_COLORS[ev.type]}15` : 'white',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: isToday ? 700 : 500,
                              fontSize: 14,
                              color: isToday ? 'var(--brand)' : 'inherit',
                            }}
                          >
                            {dayNum}
                          </div>
                          {ev && (
                            <div
                              style={{
                                fontSize: 11,
                                color: EVENT_COLORS[ev.type],
                                marginTop: 2,
                                lineHeight: 1.2,
                              }}
                            >
                              {ev.label}
                            </div>
                          )}
                          {isToday && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: 2,
                                right: 4,
                                fontSize: 10,
                                color: 'var(--brand)',
                                fontWeight: 700,
                              }}
                            >
                              วันนี้
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Schedule · กำหนดการปิดงวด</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(CAL_EVENTS).map(([dayStr, ev]) => {
              const dayNum = parseInt(dayStr, 10);
              const isPast = dayNum < today;
              const isToday = dayNum === today;
              const weekday = SCHEDULE_WEEKDAYS[dayNum - 21] ?? '-';
              return (
                <div
                  key={dayStr}
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: '10px 12px',
                    background: isToday ? 'var(--brand-tint)' : isPast ? '#fafafa' : 'white',
                    border: `1px solid ${isToday ? 'var(--brand)' : 'var(--border)'}`,
                    borderRadius: 6,
                    opacity: isPast ? 0.7 : 1,
                  }}
                >
                  <div style={{ width: 38, textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-faint)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {weekday}
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        fontFamily: 'var(--font-display)',
                      }}
                    >
                      {dayStr}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: EVENT_COLORS[ev.type] }}>
                      {ev.label}
                    </div>
                    <div className="meta" style={{ marginTop: 2 }}>
                      {isPast ? '✓ เสร็จแล้ว' : isToday ? 'วันนี้ — กำลังดำเนินการ' : 'กำหนดการ'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Close Settings — reflects live template (from current checklist) ===== */
interface CloseSettingsProps {
  data: PeriodCloseChecklistResponse | undefined;
}

function CloseSettings({ data }: CloseSettingsProps) {
  const steps = data?.steps ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>ตั้งค่า Closing Checklist · Template</h3>
          <div className="sub">
            เพิ่ม/แก้ขั้นตอน, ผู้รับผิดชอบ, เวลาที่ใช้ — ใช้กับทุกงวดในอนาคต
          </div>
        </div>
        <button className="btn sm primary">
          <Icon name="plus" size={12} /> เพิ่มขั้นตอน
        </button>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ลำดับ</th>
              <th>Phase</th>
              <th>ขั้นตอน</th>
              <th>Critical</th>
              <th>เชื่อม Module</th>
              <th>ผู้รับผิดชอบ</th>
              <th>เวลา (นาที)</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((s) => {
              const phaseUI = PHASE_TO_UI[s.phase];
              const ph = PHASES.find((p) => p.code === phaseUI);
              const phLabelParts = ph ? ph.label.split('.') : [''];
              const phLabel = phLabelParts[1] ?? '';
              const owner = SETTINGS_OWNERS[s.stepNo % SETTINGS_OWNERS.length] ?? '';
              return (
                <tr key={s.id}>
                  <td className="num">{s.stepNo}</td>
                  <td>
                    {ph?.icon} {phLabel}
                  </td>
                  <td>{s.name}</td>
                  <td>
                    {s.critical ? (
                      <span className="badge negative" style={{ fontSize: 13 }}>
                        Critical
                      </span>
                    ) : (
                      <span className="badge neutral" style={{ fontSize: 13 }}>
                        Optional
                      </span>
                    )}
                  </td>
                  <td className="muted">
                    {s.linkRoute ? <span className="code">{s.linkRoute}</span> : '—'}
                  </td>
                  <td className="muted">{owner}</td>
                  <td className="num">{s.estMin}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn sm ghost" style={{ height: 26, fontSize: 13 }}>
                        แก้ไข
                      </button>
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13, color: 'var(--negative)' }}
                      >
                        ลบ
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===== Run Recurring Modal ===== */
interface RunRecurringModalProps {
  step: PeriodCloseStep;
  onClose: () => void;
}

function RunRecurringModal({ step, onClose }: RunRecurringModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 640,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            รัน Recurring Entry
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600 }}>{step.name}</div>
            <div className="meta" style={{ marginTop: 4 }}>
              จำนวน <b>{fmt(step.refCount)}</b> รายการ · มูลค่า{' '}
              <b className="tnum">{fmt(Number(step.amount))}</b> บาท
            </div>
          </div>
          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              ระบบจะเปิด Module <b>{step.linkRoute}</b> ในหน้าต่างใหม่ ให้คุณรันการลงบัญชี ·
              เมื่อกลับมา ขั้นตอนนี้จะถูก mark เสร็จอัตโนมัติ
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button className="btn primary">
            <Icon name="check" size={14} /> เปิด Module {step.linkRoute}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Close Period Modal ===== */
interface ClosePeriodModalProps {
  summary: PeriodCloseChecklistResponse['summary'] | undefined;
  periodDisplay: { mm: string; yyyy: string; thaiMonth: string } | null;
  isClosing: boolean;
  closeError: Error | null;
  onConfirm: () => void;
  onClose: () => void;
}

function ClosePeriodModal({
  summary,
  periodDisplay,
  isClosing,
  closeError,
  onConfirm,
  onClose,
}: ClosePeriodModalProps) {
  const total = summary?.total ?? 0;
  const done = summary?.done ?? 0;
  const criticalOpen = summary?.criticalOpen ?? 0;
  const periodTag = periodDisplay
    ? `${periodDisplay.mm}/${periodDisplay.yyyy} ${periodDisplay.thaiMonth} ${periodDisplay.yyyy}`
    : '—';
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ปิดงวดบัญชี — งวด {periodDisplay ? `${periodDisplay.mm}/${periodDisplay.yyyy}` : ''}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ขั้นสุดท้าย · จะ Lock JV ห้ามแก้ย้อน + Carry forward ยอดยกมา
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            padding: '22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {criticalOpen > 0 ? (
            <div
              style={{
                padding: '16px 18px',
                background: '#fce9e7',
                border: '2px solid var(--negative)',
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--negative)',
                  marginBottom: 6,
                }}
              >
                ⛔ ไม่สามารถปิดงวดได้
              </div>
              <div style={{ fontSize: 14 }}>
                มีขั้นตอน Critical {criticalOpen} ขั้นที่ยังไม่เสร็จ · ต้องดำเนินการให้ครบก่อน
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: '16px 18px',
                background: '#e7f4ec',
                border: '2px solid var(--positive)',
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--positive)',
                  marginBottom: 6,
                }}
              >
                ✓ พร้อมปิดงวด
              </div>
              <div style={{ fontSize: 14 }}>
                ทุก Critical Step เสร็จแล้ว · กดปุ่มด้านล่างเพื่อ Lock งวดและสร้างยอดยกมา
              </div>
            </div>
          )}

          {closeError && (
            <div
              style={{
                padding: '12px 14px',
                background: '#fce9e7',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              ปิดงวดไม่สำเร็จ: {closeError.message}
            </div>
          )}

          <div className="section-title" style={{ margin: '4px 0' }}>
            สรุปก่อนปิดงวด
          </div>
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <tbody>
              <tr>
                <td>งวด</td>
                <td>
                  <b>{periodTag}</b>
                </td>
              </tr>
              <tr>
                <td>ขั้นตอนทั้งหมด</td>
                <td>{total} ขั้น</td>
              </tr>
              <tr>
                <td>เสร็จแล้ว</td>
                <td>
                  <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{done} ขั้น</span>
                </td>
              </tr>
              <tr>
                <td>Critical รอทำ</td>
                <td>
                  {criticalOpen > 0 ? (
                    <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                      {criticalOpen} ขั้น
                    </span>
                  ) : (
                    <span style={{ color: 'var(--positive)', fontWeight: 600 }}>0 ✓</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="section-title" style={{ margin: '4px 0' }}>
            การกระทำหลังกด Lock
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ACTION_AFTER_LOCK.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  fontSize: 14,
                  padding: '8px 12px',
                  background: '#fafafa',
                  borderRadius: 6,
                }}
              >
                <Icon name="check" size={16} />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isClosing}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={criticalOpen > 0 || isClosing}
            onClick={onConfirm}
          >
            <Icon name="check" size={14} />{' '}
            {isClosing
              ? 'กำลังปิดงวด…'
              : `ยืนยัน · Lock งวด ${periodDisplay ? `${periodDisplay.mm}/${periodDisplay.yyyy}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
